import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError, eur } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ArrowRight, ArrowLeft, UserPlus, Check, Printer } from "lucide-react";

const STEPS = ["Kunde", "Artikel", "Rechnung"];
const EMPTY_CUSTOMER = { first_name: "", last_name: "", company: "", email: "", phone: "", address: "", zip: "", city: "", country: "Deutschland" };

export default function VerkaufNeu() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState(EMPTY_CUSTOMER);
  const [articles, setArticles] = useState([]);
  const [selected, setSelected] = useState({});
  const [paymentMethod, setPaymentMethod] = useState("bar");
  const [channel, setChannel] = useState("laden");
  const [taxMode, setTaxMode] = useState("differenz");
  const [sale, setSale] = useState(null);
  const [busy, setBusy] = useState(false);
  const [taxRate, setTaxRate] = useState(19);
  const [shipping, setShipping] = useState("");
  const [otherCosts, setOtherCosts] = useState("");
  const [note, setNote] = useState("");
  const [saleDate, setSaleDate] = useState(new Date().toISOString().slice(0, 10));
  const [params] = useSearchParams();

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/articles?status=lager").then((r) => {
      setArticles(r.data);
      const pre = params.get("artikel");
      if (pre) {
        const a = r.data.find((x) => x.id === pre);
        if (a) setSelected({ [a.id]: a.sale_price || "" });
      }
    });
    api.get("/settings").then((r) => {
      const d = r.data.tenant?.tax_mode_default;
      if (["differenz", "regel", "frei", "ku19"].includes(d)) setTaxMode(d);
      if (r.data.tenant?.tax_rate) setTaxRate(Number(r.data.tenant.tax_rate));
    }).catch(() => {});
  }, []);

  const filteredCustomers = customers.filter((c) =>
    `${c.first_name} ${c.last_name} ${c.company} ${c.email} ${c.number}`.toLowerCase().includes(customerSearch.toLowerCase()));

  const createCustomer = async () => {
    try {
      const { data } = await api.post("/customers", newCustomer);
      setCustomers([data, ...customers]);
      setCustomer(data);
      setNewCustomerOpen(false);
      toast.success(`Kunde ${data.number} angelegt und übernommen.`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggle = (a) => {
    const next = { ...selected };
    if (next[a.id]) delete next[a.id];
    else next[a.id] = a.sale_price || "";
    setSelected(next);
  };

  const items = articles.filter((a) => selected[a.id] !== undefined);
  const total = items.reduce((s, a) => s + (parseFloat(selected[a.id]) || 0), 0);
  const profit = items.reduce((s, a) => s + (parseFloat(selected[a.id]) || 0) - (a.purchase_price || 0), 0);
  const margin = Math.max(0, profit);
  const rateFactor = 1 + taxRate / 100;
  const extraCosts = (parseFloat(shipping) || 0) + (parseFloat(otherCosts) || 0);
  const taxPreview = taxMode === "differenz" ? margin - margin / rateFactor : taxMode === "regel" ? total - total / rateFactor : 0;
  const profitAfterTax = (taxMode === "regel" ? total / rateFactor - (total - profit) : profit - taxPreview) - extraCosts;

  const createSale = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sales", {
        customer_id: customer.id, payment_method: paymentMethod, channel, tax_mode: taxMode,
        date: saleDate || undefined, shipping: parseFloat(shipping) || 0,
        costs: otherCosts ? [{ label: "Sonstige Kosten", amount: parseFloat(otherCosts) }] : [],
        note,
        items: items.map((a) => ({ article_id: a.id, sale_price: parseFloat(selected[a.id]) || 0 })),
      });
      setSale(data);
      toast.success(`Verkauf ${data.number} gebucht · Rechnung ${data.invoice.number} · Gewinn nach Steuer ${eur(data.profit_after_tax ?? data.profit)}`);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const printInvoice = async () => {
    const { data } = await api.get(`/invoices/${sale.invoice.id}/pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="verkauf-neu-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Verkauf</div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Neuer Verkauf</h1>
      </div>
      <div className="flex gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className={`flex-1 text-center text-[11px] font-mono py-2 rounded-md border ${
            step > i ? "bg-zinc-900 text-white border-zinc-900" : step === i ? "border-zinc-900 text-zinc-900" : "border-zinc-200 text-zinc-400"}`}>
            {i + 1}. {s}
          </div>
        ))}
      </div>

      {!sale && step === 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex gap-3">
            <Input placeholder="Kunde suchen (Name, Firma, E-Mail, Kundennr.)…" value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)} data-testid="customer-search-input" />
            <Button variant="outline" onClick={() => setNewCustomerOpen(true)} data-testid="new-customer-button"><UserPlus className="w-4 h-4 mr-1" /> Neuer Kunde</Button>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-zinc-50 border border-zinc-100 rounded-lg">
            {filteredCustomers.length === 0 && <div className="p-4 text-sm text-zinc-400">Keine Treffer – neuen Kunden anlegen.</div>}
            {filteredCustomers.map((c) => (
              <button key={c.id} onClick={() => setCustomer(c)} data-testid={`customer-option-${c.number}`}
                className={`w-full text-left px-4 py-3 text-sm hover:bg-zinc-50 flex justify-between items-center ${customer?.id === c.id ? "bg-zinc-900 text-white hover:bg-zinc-900" : ""}`}>
                <span>{c.first_name} {c.last_name} {c.company && <span className="opacity-60">· {c.company}</span>}</span>
                <span className="font-mono text-xs opacity-60">{c.number}</span>
              </button>
            ))}
          </div>
          {customer && <div className="text-sm bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3" data-testid="customer-selected">Ausgewählt: <b>{customer.first_name} {customer.last_name}</b> · {customer.number} · Alle gespeicherten Daten werden übernommen.</div>}
          <div className="flex justify-end">
            <Button disabled={!customer} onClick={() => setStep(1)} data-testid="verkauf-step1-next" className="bg-zinc-900 hover:bg-zinc-800 text-white">Weiter <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {!sale && step === 1 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-4">
          <div className="text-sm text-zinc-500">Verfügbare Artikel (Status: Lager) auswählen und Verkaufspreis bestätigen:</div>
          <div className="divide-y divide-zinc-50 border border-zinc-100 rounded-lg max-h-96 overflow-y-auto">
            {articles.length === 0 && <div className="p-4 text-sm text-zinc-400">Keine Artikel im Lager.</div>}
            {articles.map((a) => (
              <div key={a.id} className="px-4 py-3 flex items-center gap-3" data-testid={`sale-article-${a.number}`}>
                <input type="checkbox" checked={selected[a.id] !== undefined} onChange={() => toggle(a)} data-testid={`sale-article-check-${a.number}`} className="w-4 h-4 accent-zinc-900" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{a.name}</div>
                  <div className="text-xs font-mono text-zinc-400">{a.number} · {a.serial || a.imei || "–"} · EK {eur(a.purchase_price)}</div>
                </div>
                {selected[a.id] !== undefined && (
                  <Input type="number" step="0.01" value={selected[a.id]} onChange={(e) => setSelected({ ...selected, [a.id]: e.target.value })}
                    data-testid={`sale-article-price-${a.number}`} className="w-28" placeholder="VK €" />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center border-t border-zinc-100 pt-4 text-sm">
            <span className="text-zinc-500">{items.length} Artikel · Gewinn nach Steuer ≈ <b className="text-zinc-900">{eur(profitAfterTax)}</b></span>
            <span className="font-display text-xl font-semibold" data-testid="sale-total">{eur(total)}</span>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="w-4 h-4 mr-1" /> Zurück</Button>
            <Button disabled={items.length === 0} onClick={() => setStep(2)} data-testid="verkauf-step2-next" className="bg-zinc-900 hover:bg-zinc-800 text-white">Weiter <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {!sale && step === 2 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-5" data-testid="sale-review">
          <h3 className="font-display font-medium text-lg">Rechnung erstellen & buchen</h3>
          <div className="text-sm border border-zinc-100 rounded-lg p-4 bg-zinc-50/50 space-y-1">
            <div><span className="text-zinc-400">Kunde:</span> {customer.first_name} {customer.last_name} ({customer.number})</div>
            {items.map((a) => <div key={a.id}><span className="text-zinc-400">·</span> {a.name} — {eur(selected[a.id])}</div>)}
            <div className="pt-2 border-t border-zinc-200 font-medium flex justify-between"><span>Gesamt</span><span>{eur(total)}</span></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Zahlungsart</Label>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} data-testid="sale-payment-method" className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
                {["bar", "überweisung", "karte", "paypal"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div><Label>Verkaufskanal</Label>
              <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="sale-channel" className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
                {["laden", "online", "ebay", "sonstige"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div><Label>Steuerart</Label>
              <select value={taxMode} onChange={(e) => setTaxMode(e.target.value)} data-testid="sale-tax-mode" className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="differenz">§25a Differenzbesteuerung (Standard)</option>
                <option value="regel">19 % Umsatzsteuer</option>
                <option value="frei">0 % / steuerfrei</option>
                <option value="ku19">§19 UStG / Kleinunternehmer</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Versandkosten (€)</Label><Input type="number" step="0.01" value={shipping} onChange={(e) => setShipping(e.target.value)} data-testid="sale-shipping" className="mt-1.5" /></div>
            <div><Label>Sonstige Kosten (€)</Label><Input type="number" step="0.01" value={otherCosts} onChange={(e) => setOtherCosts(e.target.value)} data-testid="sale-other-costs" className="mt-1.5" /></div>
            <div><Label>Verkaufsdatum</Label><Input type="date" value={saleDate} onChange={(e) => setSaleDate(e.target.value)} data-testid="sale-date" className="mt-1.5" /></div>
            <div><Label>Bemerkung</Label><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="sale-note" className="mt-1.5" /></div>
          </div>
          <div className="text-sm bg-zinc-50 border border-zinc-100 rounded-lg px-4 py-3 flex justify-between">
            <span className="text-zinc-500">Gewinn nach Steuer (inkl. Kosten)</span>
            <b data-testid="sale-profit-preview">{eur(profitAfterTax)}</b>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-1" /> Zurück</Button>
            <Button onClick={createSale} disabled={busy} data-testid="sale-create-button" className="bg-zinc-900 hover:bg-zinc-800 text-white">
              {busy ? "Buche…" : "Verkauf buchen & Rechnung erstellen"} <Check className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {sale && (
        <div className="bg-white border border-zinc-200 rounded-xl p-8 shadow-sm text-center space-y-4" data-testid="sale-success">
          <div className="w-12 h-12 bg-zinc-900 text-white rounded-full flex items-center justify-center mx-auto"><Check className="w-6 h-6" /></div>
          <h3 className="font-display text-xl font-semibold">Verkauf {sale.number} abgeschlossen</h3>
          <p className="text-sm text-zinc-500">Bestand reduziert · Artikel auf „Verkauft“ · Rechnung {sale.invoice.number} erstellt · Gewinn nach Steuer {eur(sale.profit_after_tax ?? sale.profit)} · Monatsstatistik aktualisiert.</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={printInvoice} data-testid="sale-print-invoice"><Printer className="w-4 h-4 mr-2" /> Rechnung PDF</Button>
            <Button onClick={() => navigate("/app/verkauf")} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="sale-finish">Zu den Verkäufen</Button>
          </div>
        </div>
      )}

      <Dialog open={newCustomerOpen} onOpenChange={setNewCustomerOpen}>
        <DialogContent data-testid="new-customer-dialog">
          <DialogHeader><DialogTitle>Neuen Kunden anlegen</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries({ first_name: "Vorname", last_name: "Nachname", company: "Firma", email: "E-Mail", phone: "Telefon", address: "Adresse", zip: "PLZ", city: "Ort" }).map(([k, l]) => (
              <div key={k} className={k === "address" || k === "company" ? "col-span-2" : ""}>
                <Label>{l}</Label>
                <Input value={newCustomer[k]} onChange={(e) => setNewCustomer({ ...newCustomer, [k]: e.target.value })} data-testid={`new-customer-${k}`} className="mt-1.5" />
              </div>
            ))}
          </div>
          <Button onClick={createCustomer} disabled={!newCustomer.last_name && !newCustomer.company} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white mt-4" data-testid="create-customer-button">
            Kunde speichern & übernehmen
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
